/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isCPF } from 'validation-br';
import { EnvelopeSimple, Phone } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { useFetch } from '../../../hooks/useFetch';
import { FormBox } from '../../formBox';
import { PageTitle } from '../../PageTitle';
import AlertModal from '../../AlertModal';
import { FormHeading } from '../../formHeading';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { MaskedInputForm } from '../../inputs/forms/MaskedInputForm';

interface DecisionFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: string;
  placeholder?: string;
  label: string | undefined;
  yup: any;
}

export default function ProfileForm({ defaultValues = {} }: DecisionFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/client';
  const { requestApi, loading, error } = useFetch(baseUrl, { actionFeedback: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [images, setImages] = useState<any>('');
  const [isUpping, setIsUpping] = useState<any>('');
  const navigate = useNavigate();

  const optionsGenre = [
    {
      label: 'Masculino',
      value: 'M',
    },
    {
      label: 'Feminino',
      value: 'F',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'email',
      value: defaultValues.email ?? '',
      placeholder: 'Email',
      label: 'Email',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'cell_phone',
      value: defaultValues.cell_phone ?? '',
      placeholder: '(99) 99999-9999',
      label: 'Telefone',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .matches(/^\([1-9]{2}\) (9[1-9])[0-9]{3}-[0-9]{4}/, 'Telefone inválido'),
    },
    {
      name: 'taxpayer',
      value: defaultValues.taxpayer ?? '',
      placeholder: '999.999.999-99',
      label: 'CPF',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .test('taxpayer', 'CPF inválido', (val) => val !== undefined && isCPF(val)),
    },
    {
      name: 'genre',
      value: defaultValues?.genre
        ? optionsGenre.find((item) => item.value === defaultValues.genre)
        : null,
      placeholder: 'Gênero do usuário',
      label: 'Gênero',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'image',
      value: defaultValues.image?.medium ?? null,
      placeholder: '',
      label: 'Foto de perfil',
      yup: Yup.mixed().nullable(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const email = formInputs.find((field) => field.name === 'email');
  const cellphone = formInputs.find((field) => field.name === 'cell_phone');
  const taxpayer = formInputs.find((field) => field.name === 'taxpayer');
  const genre = formInputs.find((field) => field.name === 'genre');
  const image = formInputs.find((field) => field.name === 'image');

  const onSubmit = async (values: any) => {
    const profileValues = { ...values, genre: values.genre.value };
    delete profileValues.image;
    const { status: resStatus, data } = await requestApi('put', profileValues);
    if ((resStatus === 200 || resStatus === 201) && images) {
      const apiUrlId = data?.data?.id;
      await requestApi('put', { file: images.toString() }, `/v1/admin/users/${apiUrlId}/images`);
    }
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <PageTitle>Perfil</PageTitle>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!error ? 'Perfil editado com sucesso!' : 'Erro ao editar o perfil!'}
          </Text>
        }
        msgStatus={error ? 'error' : 'success'}
        closeOnOverlayClick={!!error}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => {
              onClose();
              if (!error) {
                navigate(0);
              }
            }}
          >
            Fechar
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Pessoais</FormHeading>
        <Flex direction="column" justify="space-evenly">
          <ImageInputForm
            defaultVal={defaultValues.image}
            setImages={setImages}
            setIsUpping={setIsUpping}
            name={image?.name}
            label={image?.label}
            useAvatar
            register={register}
            watch={watch}
            errors={errors}
          />

          <Flex gap={5}>
            <Box flex={2}>
              <InputForm
                name={name?.name}
                label={name?.label}
                placeholder={name?.placeholder}
                register={register}
                errors={errors}
                isRequired
              />
            </Box>

            <Box flex={1}>
              <SelectInputForm
                name={genre?.name}
                label={genre?.label}
                options={optionsGenre}
                isRequired
                placeholder={genre?.placeholder}
                control={control}
                errors={errors}
              />
            </Box>

            <Box flex={2}>
              <MaskedInputForm
                name={taxpayer?.name}
                label={taxpayer?.label}
                placeholder={taxpayer?.placeholder}
                control={control}
                errors={errors}
                mask="999.999.999-99"
                isRequired
              />
            </Box>
          </Flex>

          <FormHeading>Contato</FormHeading>

          <Flex gap={5}>
            <Box flex={1}>
              <InputForm
                name={email?.name}
                label={email?.label}
                placeholder={email?.placeholder}
                register={register}
                errors={errors}
                isRequired
                iconLeft={<EnvelopeSimple />}
              />
            </Box>

            <Box flex={1}>
              <MaskedInputForm
                name={cellphone?.name}
                label={cellphone?.label}
                placeholder={cellphone?.placeholder}
                control={control}
                errors={errors}
                mask="(99) 99999-9999"
                isRequired
                iconLeft={<Phone />}
              />
            </Box>
          </Flex>

          <FormButton
            mt={4}
            isLoading={isSubmitting || loading || isUpping}
            type="submit"
            w="100px"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
